import React, { useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GalleryDiorama } from '@/design-system'
import { DiscoverFeedItem } from '../../hooks/useDiscoverFeed'
import { resolveGalleryTint } from '../../utils/galleryTint'
import { CoverMesh } from '../CoverMesh'

export interface TrendingShelfProps {
  items: DiscoverFeedItem[]
  onSelect: (item: DiscoverFeedItem) => void
}

const Root = styled(Box)({})

const HeadRow = styled(Box)({ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 })

const HeadTitle = styled(Typography)({ fontSize: 16, fontWeight: 720, letterSpacing: '-0.02em', color: 'var(--ds-ink)' })

const MonoNote = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.11em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
})

const NavGroup = styled(Box)({ marginLeft: 'auto', display: 'flex', gap: 7 })

const NavBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 30,
  height: 30,
  borderRadius: 9,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  color: 'var(--ds-ink-muted)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border)',
  transition: 'all 140ms var(--ds-ease)',
  '&:hover': { boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)', color: 'var(--ds-ink)', background: 'var(--ds-canvas)' },
})

const Row = styled(Box)({
  display: 'flex',
  gap: 26,
  overflowX: 'auto',
  padding: '4px 2px 8px',
  scrollSnapType: 'x mandatory',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

const Item = styled(Box)({
  flex: '0 0 268px',
  scrollSnapAlign: 'start',
  display: 'flex',
  gap: 2,
  cursor: 'pointer',
  '&:hover .rank': { WebkitTextStrokeColor: 'var(--ds-accent-deep-b)' },
  '&:hover .cov-img, &:hover .cov-dio': { transform: 'scale(1.04)' },
})

const Rank = styled('span')({
  flexShrink: 0,
  width: 46,
  fontSize: 62,
  fontWeight: 800,
  lineHeight: 0.8,
  letterSpacing: '-0.05em',
  color: 'transparent',
  WebkitTextStroke: '1.5px var(--ds-border-strong)',
  alignSelf: 'flex-end',
  paddingBottom: 44,
  textAlign: 'center',
  transition: 'text-stroke-color 260ms var(--ds-ease), -webkit-text-stroke-color 260ms var(--ds-ease)',
})

const Cov = styled(Box)({
  display: 'block',
  position: 'relative',
  aspectRatio: '16 / 10.5',
  borderRadius: 14,
  overflow: 'hidden',
  background: 'var(--ds-surface)',
  boxShadow: '0 0 0 1px rgba(13,13,15,0.05)',
})

const CovImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'transform 420ms var(--ds-ease-out)',
})

const DioWrap = styled(Box)({ width: '100%', height: '100%', transition: 'transform 420ms var(--ds-ease-out)' })

const Meta = styled(Box)({ padding: '10px 2px 0', flex: 1, minWidth: 0 })

const ItemTitle = styled(Typography)({
  fontSize: 13.5,
  fontWeight: 660,
  letterSpacing: '-0.012em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'var(--ds-ink)',
})

const Sub = styled(Typography)({
  fontSize: 11.5,
  color: 'var(--ds-ink-subtle)',
  marginTop: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

/**
 * "Trending this week" — a horizontal shelf of the mixed feed's top items
 * by likes, with big outlined rank numerals (a light, classy take on a
 * Netflix top-10 rail). Mixes albums and galleries in one ranking.
 */
export function TrendingShelf({ items, onSelect }: TrendingShelfProps) {
  const rowRef = useRef<HTMLDivElement | null>(null)
  if (items.length === 0) return null

  const scrollBy = (delta: number) => rowRef.current?.scrollBy({ left: delta, behavior: 'smooth' })

  return (
    <Root>
      <HeadRow>
        <HeadTitle>Trending this week</HeadTitle>
        <MonoNote>By likes, last 7 days &mdash; not personalized</MonoNote>
        <NavGroup>
          <NavBtn aria-label='Scroll left' onClick={() => scrollBy(-600)}>&lsaquo;</NavBtn>
          <NavBtn aria-label='Scroll right' onClick={() => scrollBy(600)}>&rsaquo;</NavBtn>
        </NavGroup>
      </HeadRow>
      <Row ref={rowRef}>
        {items.map((item, i) => {
          const summary = item.kind === 'album' ? item.album : item.gallery
          if (!summary) return null
          const isGallery = item.kind === 'gallery' && item.gallery
          const sub = item.kind === 'album' ? `${item.album?.photoCount ?? 0} photos` : `${item.gallery?.printCount ?? 0} prints`

          return (
            <Item key={item.id} role='button' tabIndex={0} onClick={() => onSelect(item)} onKeyDown={(e) => e.key === 'Enter' && onSelect(item)}>
              <Rank className='rank'>{i + 1}</Rank>
              <Meta>
                <Cov>
                  {isGallery && item.gallery ? (
                    <DioWrap className='cov-dio'>
                      <GalleryDiorama
                        wallColor={resolveGalleryTint(item.gallery).wallColor}
                        floorColor={resolveGalleryTint(item.gallery).floorColor}
                        arts={item.gallery.arts}
                        light={resolveGalleryTint(item.gallery).light}
                      />
                    </DioWrap>
                  ) : item.album?.coverUrl ? (
                    <CovImg className='cov-img' src={item.album.coverUrl} alt='' loading='lazy' />
                  ) : (
                    <CoverMesh />
                  )}
                </Cov>
                <ItemTitle>{summary.title}</ItemTitle>
                <Sub>
                  {summary.owner?.name}
                  {summary.owner ? ' \u00B7 ' : ''}
                  <span style={{ color: 'var(--ds-accent-dot-2)' }}>&#9829;</span> {item.likes} &middot; {sub}
                </Sub>
              </Meta>
            </Item>
          )
        })}
      </Row>
    </Root>
  )
}
